/**
 * AnalyseTrade — /league/:id/trade/analyse (Trade Analyser split, Job 1)
 *
 * Views: picker/builder → result (PROPOSE / ALTER / DONT_PROPOSE).
 *
 * Every player, roster and trade suggestion on this page comes from the
 * user's actual connected league — real managers, real rosters. There is no
 * mock/placeholder data path: if the league hasn't drafted yet, or JAI can't
 * read a roster, the page says so instead of showing a fake trade.
 *
 * The verdict, acceptance %, and impact deltas are all computed server-side
 * in code (tradeMathService.js) — this page never parses prose to find them.
 *
 * Two ways a trade arrives pre-filled:
 *  - `?give=Name` — the older single-value deep link (Player/Team Analyser,
 *    Draft Review's "trade" recommendation link here).
 *  - router location state `{ fromRadar, targetTeamId, givingNames,
 *    receivingNames }` — Trade Radar's "Analyse this trade →" handoff, which
 *    used to mutate sibling state and flip an internal tab; now it navigates
 *    here with the full multi-player trade attached as nav state (query
 *    params only ever carried one name, so state was the natural fit for a
 *    give/get list). The state is cleared with a `replace` once read, so
 *    back/refresh doesn't replay a stale prefill.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { leaguesApi } from '../../services/api'
import { hapticTradeVerdict } from '../../services/haptics'
import { shareText } from '../../services/share'
import { IS_NATIVE_APP } from '../../services/runtime'
import AppShell from '../../components/AppShell'
import ToolHeader from '../../components/ToolHeader'
import PlayerPhoto from '../../components/PlayerPhoto'
import StickyAskBar from '../../components/StickyAskBar'
import StatTable from '../../components/StatTable'
import TeamChip from '../../components/TeamChip'
import { JAI_FONTS } from '../../components/JaiBrand'
import { V2, V2Card, V2Icons, JaiSummaryCard, PrimaryBtn, SecondaryBtn, PosChip, pctTone, Notice } from '../../components/JaiV2'
import { JAIMascot } from '../../components/JAIMascot'
import { STAT_GLYPHS } from '../../config/statGlyphs'
import { addNote } from '../../services/notesStore'
import { useDraftGate } from '../../hooks/useDraftGate'
import {
  useMinWidth, GIVE_LABEL, GET_LABEL, VERDICT_META, DraftPendingState,
  NBA_STAT_COLUMNS, nbaStatRows, nflStatColumns, nflStatRows, headlineStat,
  BulletList, ImpactRow,
} from '../../components/trade/tradeShared'

// ── Main component ─────────────────────────────────────────────────────

export default function AnalyseTrade() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const wide = useMinWidth(900)

  const { league, loading: leagueLoading, draftPending } = useDraftGate(id)
  const sport = (league?.sport || 'nba').toLowerCase()

  // My roster + every other manager in the league — only this page needs
  // these (Radar's own suggestions carry player names, not full rosters).
  const [roster, setRoster]   = useState([])
  const [teams, setTeams]     = useState([])
  const [rosterLoading, setRosterLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false
    setRosterLoading(true)
    setLoadError('')
    Promise.allSettled([leaguesApi.getRoster(id), leaguesApi.getTeams(id)])
      .then(([rosterRes, teamsRes]) => {
        if (cancelled) return
        if (rosterRes.status === 'fulfilled') setRoster(rosterRes.value.data.players || [])
        if (teamsRes.status === 'fulfilled') setTeams((teamsRes.value.data.teams || []).filter(t => !t.isMine))
        const failed = [rosterRes, teamsRes].find(r => r.status === 'rejected')
        if (failed) {
          console.error('Trade Analyser: league data failed to load', failed.reason)
          setLoadError(
            failed.reason?.response?.data?.error ||
            "JAI couldn't load your league's rosters. Try syncing the league from its page, then reload."
          )
        }
      })
      .finally(() => { if (!cancelled) setRosterLoading(false) })
    return () => { cancelled = true }
  }, [id])

  const loading = leagueLoading || rosterLoading

  // Trade partner + their real roster
  const [targetTeamId, setTargetTeamId] = useState('')
  const [targetRoster, setTargetRoster] = useState([])
  const [targetRosterLoading, setTargetRosterLoading] = useState(false)

  // Selections — arrays of real player names, each resolvable on its own roster
  const [givingNames, setGivingNames]       = useState([])
  const [receivingNames, setReceivingNames] = useState([])
  const [expandedGiving, setExpandedGiving]     = useState(() => new Set())
  const [expandedReceiving, setExpandedReceiving] = useState(() => new Set())

  // Analyse Your Trade result
  const [result, setResult]       = useState(null)
  const [analysing, setAnalysing] = useState(false)
  const [error, setError]         = useState('')

  // JAI-generated alternative (Alter / Generate / Request new)
  const [generated, setGenerated]   = useState(null)
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError]     = useState('')
  const [excludeList, setExcludeList] = useState([])
  const [savedNote, setSavedNote]   = useState(false)
  const [savingNote, setSavingNote] = useState(false)
  const [saveNoteError, setSaveNoteError] = useState('')

  // ── Prefill — either Trade Radar's handoff (nav state) or the older
  // ?give=Name deep link (Player/Team Analyser, Draft Review) ────────────

  useEffect(() => {
    const state = location.state
    if (state?.fromRadar) {
      setTargetTeamId(String(state.targetTeamId))
      setGivingNames(state.givingNames?.length ? [...state.givingNames] : [])
      setReceivingNames(state.receivingNames?.length ? [...state.receivingNames] : [])
      // Clear the nav state so a refresh or back-nav doesn't replay it.
      navigate(location.pathname, { replace: true, state: null })
    } else {
      const give = searchParams.get('give')
      if (give) setGivingNames([give])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Fetch the target manager's real roster once one is picked ─────────

  useEffect(() => {
    if (!targetTeamId) { setTargetRoster([]); return }
    let cancelled = false
    setTargetRosterLoading(true)
    leaguesApi.getTeamRoster(id, targetTeamId)
      .then(res => { if (!cancelled) setTargetRoster(res.data.players || []) })
      .catch((err) => {
        console.error('Trade Analyser: target roster failed to load', err)
        if (!cancelled) setTargetRoster([])
      })
      .finally(() => { if (!cancelled) setTargetRosterLoading(false) })
    return () => { cancelled = true }
  }, [id, targetTeamId])

  const selectTeam = (teamId) => {
    setTargetTeamId(prev => {
      const next = String(prev) === String(teamId) ? '' : String(teamId)
      return next
    })
    setReceivingNames([])   // old selections belonged to a different roster
    setExpandedReceiving(new Set())
  }

  const toggleGiving = (name) => setGivingNames(p => p.includes(name) ? p.filter(n => n !== name) : [...p, name])
  const toggleReceiving = (name) => setReceivingNames(p => p.includes(name) ? p.filter(n => n !== name) : [...p, name])
  const toggleExpand = (setFn) => (name) => setFn(prev => {
    const next = new Set(prev)
    if (next.has(name)) next.delete(name); else next.add(name)
    return next
  })

  const canAnalyse = !analysing && !!targetTeamId && givingNames.length > 0 && receivingNames.length > 0

  // ── Analyse ─────────────────────────────────────────────────────────

  const analyse = async () => {
    if (!canAnalyse) return
    setAnalysing(true)
    setError('')
    setResult(null)
    setGenerated(null)
    setExcludeList([])
    setSavedNote(false)
    try {
      const res = await leaguesApi.analyseTrade(id, givingNames, receivingNames, targetTeamId)
      setResult(res.data)
      hapticTradeVerdict()
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.')
    } finally {
      setAnalysing(false)
    }
  }

  const startOver = () => {
    setResult(null)
    setGenerated(null)
    setGenError('')
    setExcludeList([])
    setSavedNote(false)
  }

  const alterManually = () => {
    // Clears the result and selections, keeps the target team, returns to the picker.
    setResult(null)
    setGenerated(null)
    setGenError('')
    setExcludeList([])
    setSavedNote(false)
    setGivingNames([])
    setReceivingNames([])
  }

  // ── JAI generate / alter / request-new — one call, three entry points ──

  const runGenerate = useCallback(async (seedGiving, seedReceiving, addToExclude) => {
    setGenerating(true)
    setGenError('')
    const nextExclude = addToExclude ? [...excludeList, addToExclude] : excludeList
    try {
      const res = await leaguesApi.generateTrade(id, { targetTeamId, seedGiving, seedReceiving, exclude: nextExclude })
      if (!res.data?.verdict) {
        setGenError(res.data?.message || "JAI couldn't find another realistic trade with this manager right now.")
        setGenerated(null)
      } else {
        setGenerated(res.data)
        setExcludeList(nextExclude)
        setSavedNote(false)
      }
    } catch (err) {
      setGenError(err.response?.data?.error || 'Something went wrong generating a trade.')
    } finally {
      setGenerating(false)
    }
  }, [id, targetTeamId, excludeList])

  const letJaiAlter = () => {
    const seedGiving = result.giving.map(p => p.name)
    const seedReceiving = result.receiving.map(p => p.name)
    runGenerate(seedGiving, seedReceiving, { giving: seedGiving, receiving: seedReceiving })
  }

  const requestNewTrade = () => {
    const seedGiving = generated.giving.map(p => p.name)
    const seedReceiving = generated.receiving.map(p => p.name)
    runGenerate(seedGiving, seedReceiving, { giving: seedGiving, receiving: seedReceiving })
  }

  const generateAnother = () => {
    // PROPOSE's "Generate New Trade" — same mechanic, excludes the trade just shown.
    const seedGiving = result.giving.map(p => p.name)
    const seedReceiving = result.receiving.map(p => p.name)
    runGenerate([], [], { giving: seedGiving, receiving: seedReceiving })
  }

  // ── Save to My Notes ────────────────────────────────────────────────

  const saveToNotes = async (r) => {
    if (savedNote || savingNote) return
    const givingLine = r.giving.map(p => p.name).join(', ')
    const receivingLine = r.receiving.map(p => p.name).join(', ')
    const targetName = r.targetTeam?.name || 'the other manager'
    const text = [
      `Trade proposal — ${new Date().toLocaleDateString()}`,
      `${league?.name || 'League'} · vs ${targetName}`,
      `You give: ${givingLine}`,
      `You get: ${receivingLine}`,
      `JAI verdict: ${VERDICT_META[r.verdict]?.label || r.verdict} (${r.acceptanceScore}%)`,
      r.summary ? `Summary: ${r.summary}` : null,
    ].filter(Boolean).join('\n')
    setSavingNote(true)
    setSaveNoteError('')
    try {
      await addNote(id, text, { source: 'trade-analyser', title: `Trade idea — ${givingLine} for ${receivingLine}` })
      setSavedNote(true)
    } catch {
      setSaveNoteError('Could not save this to your notes. Try again.')
    } finally {
      setSavingNote(false)
    }
  }

  const targetManagerName = teams.find(t => String(t.id) === String(targetTeamId))?.name || null

  // Context for the sticky ask bar — only meaningful once a trade's been
  // graded; before that JAI still answers, just without this extra context
  // (same graceful pattern StickyAskBar's other callers use).
  const askBarContext = useMemo(() => {
    if (!result) return undefined
    const { giving, receiving, summary } = result
    return `THE USER IS LOOKING AT A GRADED TRADE RIGHT NOW.\n` +
      `In ${league?.name || 'their fantasy league'}${targetManagerName ? ` with ${targetManagerName}` : ''}: ` +
      `giving ${giving.map(p => p.name).join(', ')} for ${receiving.map(p => p.name).join(', ')}.\n` +
      `JAI's summary: ${summary || ''}`
  }, [result, league, targetManagerName])

  // ── Loading ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <p className="text-sm" style={{ color: V2.faint }}>Loading…</p>
        </div>
      </AppShell>
    )
  }

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <ToolHeader
            leagueName={league?.name}
            title="Analyse Your Trade"
            subtitle="Pick a manager and a trade — JAI grades it against both real rosters."
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
            backRight={{ label: 'Go to Trade Analyser', to: `/league/${id}/trade` }}
          />

          {draftPending ? (
            <DraftPendingState />
          ) : (
            result ? (
              <ResultPanel
                result={result} generated={generated} generating={generating} genError={genError}
                onLetJaiAlter={letJaiAlter} onRequestNew={requestNewTrade} onGenerateAnother={generateAnother}
                onAlterManually={alterManually} onStartOver={startOver}
                onSave={saveToNotes} savedNote={savedNote} savingNote={savingNote} saveNoteError={saveNoteError}
                targetManagerName={targetManagerName}
              />
            ) : (
              <BuilderPanel
                teams={teams} targetTeamId={targetTeamId} onSelectTeam={selectTeam}
                roster={roster} targetRoster={targetRoster} targetRosterLoading={targetRosterLoading}
                givingNames={givingNames} receivingNames={receivingNames}
                onToggleGiving={toggleGiving} onToggleReceiving={toggleReceiving}
                expandedGiving={expandedGiving} expandedReceiving={expandedReceiving}
                onToggleExpandGiving={toggleExpand(setExpandedGiving)} onToggleExpandReceiving={toggleExpand(setExpandedReceiving)}
                sport={sport} wide={wide} myTeamName={league?.teamName}
                canAnalyse={canAnalyse} analysing={analysing} onAnalyse={analyse} error={error || loadError}
              />
            )
          )}
        </div>
      </div>

      {/* Only once JAI's actually graded a trade — on the picker/builder page
          it would float over the "Analyse Trade" button at the bottom of the
          screen and block it. */}
      {result && (
        <StickyAskBar
          leagueId={id}
          scope="trade"
          context={askBarContext}
          placeholder="Ask JAI about this trade"
        />
      )}
    </AppShell>
  )
}

// ── Trading With: card grid, no dropdown ────────────────────────────────

function TeamCardGrid({ teams, value, onSelect, wide }) {
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: V2.navy, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10 }}>Trading with</div>
      {teams.length === 0 ? (
        <p style={{ fontSize: 12.5, color: V2.faint }}>JAI couldn't find other managers in this league yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: wide ? '1fr 1fr' : '1fr', gap: 10 }}>
          {teams.map(t => {
            const selected = String(value) === String(t.id)
            const record = t.wins != null ? `${t.wins}-${t.losses}${t.ties ? `-${t.ties}` : ''}` : null
            return (
              <button
                key={t.id}
                onClick={() => onSelect(t.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                  background: selected ? V2.navy : 'white',
                  border: `2px solid ${selected ? V2.navy : V2.hairSoft}`,
                  borderRadius: 14, padding: '12px 16px', cursor: 'pointer',
                  fontFamily: JAI_FONTS.tight, transition: 'border-color 150ms, background 150ms',
                }}
              >
                {t.abbrev && (
                  <span style={{
                    fontSize: 10.5, fontWeight: 800, fontFamily: JAI_FONTS.mono, letterSpacing: '0.04em',
                    color: selected ? 'white' : V2.faint,
                    border: `1px solid ${selected ? 'rgba(255,255,255,0.35)' : V2.hairSoft}`,
                    borderRadius: 6, padding: '3px 6px', flexShrink: 0,
                  }}>
                    {t.abbrev}
                  </span>
                )}
                <span style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: selected ? 'white' : V2.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.name || `Team ${t.id}`}
                  </div>
                  {record && (
                    <div style={{ fontSize: 11.5, color: selected ? 'rgba(255,255,255,0.65)' : V2.faint, marginTop: 1 }}>
                      {record}
                    </div>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── You Give / You Get roster row ───────────────────────────────────────

function RosterRow({ player, sport, selected, onToggleSelect, expanded, onToggleExpand }) {
  const injured = player.injuryStatus && player.injuryStatus !== 'ACTIVE'
  const strip = headlineStat(player, sport)
  const columns = sport === 'nfl' ? nflStatColumns(player) : NBA_STAT_COLUMNS
  const rows = sport === 'nfl' ? nflStatRows(player, columns) : nbaStatRows(player)

  return (
    <div style={{
      borderBottom: `1px solid ${V2.hairSoft}`,
      borderLeft: selected ? `2px solid ${V2.blue}` : '2px solid transparent',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => onToggleSelect(player.name)}
          style={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 11,
            padding: '11px 10px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
          }}
        >
          <PlayerPhoto playerId={player.id ?? player.playerId} name={player.name} size={34} rounded="full" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: selected ? V2.blue : V2.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {player.name}
              </span>
              {injured && <span title={player.injuryStatus} style={{ fontSize: 12 }}>{STAT_GLYPHS.injury}</span>}
              <PosChip>{player.position}</PosChip>
              <TeamChip abbr={player.team} sport={sport} />
            </div>
            {strip && (
              <div style={{ fontSize: 11.5, color: V2.faint, marginTop: 2, fontFamily: JAI_FONTS.mono }}>{strip}</div>
            )}
          </div>
        </button>
        <button
          onClick={() => onToggleExpand(player.name)}
          aria-label="Expand stats"
          style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: V2.faint, padding: '10px 12px', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
        >
          {V2Icons.chevronDown}
        </button>
      </div>
      {expanded && (
        <div style={{ padding: '0 10px 12px' }}>
          <StatTable title={`${player.name}'s stats`} columns={columns} rows={rows} />
        </div>
      )}
    </div>
  )
}

function RosterColumn({ label, players, selectedNames, onToggleSelect, expanded, onToggleExpand, sport, disabledMessage }) {
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: V2.navy, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10 }}>{label}</div>
      <div style={{ background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 14, overflow: 'hidden' }}>
        {disabledMessage ? (
          <div style={{ fontSize: 12.5, color: V2.faint, padding: '16px 14px', lineHeight: 1.5 }}>{disabledMessage}</div>
        ) : players.length === 0 ? (
          <div style={{ fontSize: 12.5, color: V2.faint, padding: '16px 14px' }}>No roster data available.</div>
        ) : (
          players.map(p => (
            <RosterRow
              key={p.name} player={p} sport={sport}
              selected={selectedNames.includes(p.name)} onToggleSelect={onToggleSelect}
              expanded={expanded.has(p.name)} onToggleExpand={onToggleExpand}
            />
          ))
        )}
      </div>
    </div>
  )
}

// ── Trade summary ────────────────────────────────────────────────────────

function TradeSummaryList({ label, names, roster, sport }) {
  const players = names.map(n => roster.find(p => p.name === n)).filter(Boolean)
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.faint, marginBottom: 8 }}>{label}</div>
      {players.length === 0 ? (
        <p style={{ fontSize: 12.5, color: V2.faint, margin: 0 }}>
          {label === GIVE_LABEL ? 'Select players from your roster' : 'Select players from their roster'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {players.map(p => (
            <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ fontWeight: 700, color: V2.navy }}>{p.name}</span>
              <PosChip>{p.position}</PosChip>
              <TeamChip abbr={p.team} sport={sport} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Builder panel (picker + You Give/Get + summary + Analyse button) ────

function BuilderPanel({
  teams, targetTeamId, onSelectTeam, roster, targetRoster, targetRosterLoading,
  givingNames, receivingNames, onToggleGiving, onToggleReceiving,
  expandedGiving, expandedReceiving, onToggleExpandGiving, onToggleExpandReceiving,
  sport, wide, myTeamName, canAnalyse, analysing, onAnalyse, error,
}) {
  const targetDisabledMessage = !targetTeamId
    ? 'Pick a manager above to search their roster.'
    : targetRosterLoading ? 'Loading their roster…'
    : targetRoster.length === 0 ? "JAI couldn't read that manager's roster right now."
    : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <TeamCardGrid teams={teams} value={targetTeamId} onSelect={onSelectTeam} wide={wide} />

      <div style={{ display: 'grid', gridTemplateColumns: wide ? '1fr 1fr' : '1fr', gap: 16 }}>
        <RosterColumn
          label={`${GIVE_LABEL} (${myTeamName || 'your team'})`} players={roster}
          selectedNames={givingNames} onToggleSelect={onToggleGiving}
          expanded={expandedGiving} onToggleExpand={onToggleExpandGiving} sport={sport}
        />
        <RosterColumn
          label={GET_LABEL} players={targetTeamId ? targetRoster : []}
          selectedNames={receivingNames} onToggleSelect={onToggleReceiving}
          expanded={expandedReceiving} onToggleExpand={onToggleExpandReceiving} sport={sport}
          disabledMessage={targetDisabledMessage}
        />
      </div>

      <V2Card pad="16px 18px">
        <div style={{ display: 'flex', gap: 20 }}>
          <TradeSummaryList label={GIVE_LABEL} names={givingNames} roster={roster} sport={sport} />
          <TradeSummaryList label={GET_LABEL} names={receivingNames} roster={targetRoster} sport={sport} />
        </div>
      </V2Card>

      {error && (
        <Notice tone="error">{error}</Notice>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
        <JAIMascot size={52} state={analysing ? 'thinking' : 'idle'} animate={analysing} />
        <PrimaryBtn onClick={onAnalyse} disabled={!canAnalyse} credits="tradeAnalyser" style={{ padding: '13px 28px', fontSize: 13.5, borderRadius: 11 }}>
          {analysing ? 'Analysing…' : 'Analyse Trade'}
        </PrimaryBtn>
      </div>
    </div>
  )
}

// ── Result panel (mascot header + verdict-specific body) ────────────────

function MascotVerdictHeader({ verdict, acceptanceScore }) {
  const meta = VERDICT_META[verdict] || VERDICT_META.ALTER
  return (
    <div style={{ textAlign: 'center', marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
        <JAIMascot size={72} state={meta.mascot} animate />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: V2.navy, marginBottom: 4 }}>{meta.label}</div>
      <div style={{ fontSize: 40, fontWeight: 800, color: pctTone(acceptanceScore), fontFamily: JAI_FONTS.mono, letterSpacing: '-0.02em' }}>
        {acceptanceScore}%
      </div>
    </div>
  )
}

function ProsConsGrid({ pros, cons }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: V2.success, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Pros for you</div>
        <BulletList items={pros.you} color={V2.navy} dotColor={V2.success} />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: V2.error, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Cons for you</div>
        <BulletList items={cons.you} color={V2.navy} dotColor={V2.error} />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: V2.success, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8, marginTop: 6 }}>Pros for them</div>
        <BulletList items={pros.them} color={V2.navy} dotColor={V2.success} />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: V2.error, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8, marginTop: 6 }}>Cons for them</div>
        <BulletList items={cons.them} color={V2.navy} dotColor={V2.error} />
      </div>
    </div>
  )
}

function TradePlayersStrip({ giving, receiving }) {
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: V2.faint, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>You give</div>
        {giving.map(p => (
          <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <PlayerPhoto playerId={p.id ?? p.playerId} name={p.name} size={28} rounded="full" />
            <span style={{ fontSize: 13, fontWeight: 700, color: V2.navy }}>{p.name}</span>
            <PosChip>{p.position}</PosChip>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: V2.faint, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>You get</div>
        {receiving.map(p => (
          <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <PlayerPhoto playerId={p.id ?? p.playerId} name={p.name} size={28} rounded="full" />
            <span style={{ fontSize: 13, fontWeight: 700, color: V2.navy }}>{p.name}</span>
            <PosChip>{p.position}</PosChip>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Shared between the ALTER and DONT_PROPOSE flows — built once, not twice. */
function JaiGeneratedTrade({ generated, generating, genError, onRequestNew, onSave, saved, saving, saveError }) {
  if (generating) {
    return (
      <V2Card pad="24px" style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <JAIMascot size={48} state="thinking" animate />
        </div>
        <p style={{ fontSize: 12.5, color: V2.faint, marginTop: 10 }}>Building a trade…</p>
      </V2Card>
    )
  }
  if (genError && !generated) {
    return (
      <V2Card pad="20px" style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: V2.mute }}>{genError}</p>
      </V2Card>
    )
  }
  if (!generated) return null

  return (
    <V2Card pad="18px 20px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <JAIMascot size={30} state="idle" animate={false} />
        <span style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, letterSpacing: '0.06em', textTransform: 'uppercase' }}>JAI's alternative</span>
        <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 800, fontFamily: JAI_FONTS.mono, color: pctTone(generated.acceptanceScore) }}>
          {generated.acceptanceScore}%
        </span>
      </div>

      <TradePlayersStrip giving={generated.giving} receiving={generated.receiving} />

      {generated.reasons?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: V2.blue, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Why this is better</div>
          <BulletList items={generated.reasons} dotColor={V2.blue} />
        </div>
      )}

      {generated.impacts?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          {generated.impacts.map(row => <ImpactRow key={row.key} {...row} />)}
        </div>
      )}

      <p style={{ fontSize: 12.5, color: V2.mute, lineHeight: 1.6, margin: '0 0 14px' }}>{generated.summary}</p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <SecondaryBtn onClick={onRequestNew} style={{ fontSize: 12 }}>Request a New Trade</SecondaryBtn>
        <PrimaryBtn onClick={() => onSave(generated)} disabled={saved || saving} style={{ fontSize: 12 }}>
          {saved ? 'Saved to My Notes' : saving ? 'Saving…' : 'Confirm Trade Proposal & Save to My Notes'}
        </PrimaryBtn>
        {saveError && <span style={{ fontSize: 12, color: V2.error, alignSelf: 'center' }}>{saveError}</span>}
      </div>
    </V2Card>
  )
}

function ResultPanel({
  result, generated, generating, genError,
  onLetJaiAlter, onRequestNew, onGenerateAnother, onAlterManually, onStartOver,
  onSave, savedNote, savingNote, saveNoteError, targetManagerName,
}) {
  const { verdict, acceptanceScore, unfairTo, pros, cons, impacts, summary, giving, receiving } = result

  // Native-only (Guideline 4.2 — a real native feature, not a repackaged
  // website). Headline + a couple of lines + "via JAI" — no logos, no link
  // to pricing or anything sellable, per the Phase 4 brief.
  const handleShare = () => {
    const meta = VERDICT_META[verdict] || VERDICT_META.ALTER
    const givingLine = giving.map(p => p.name).join(', ')
    const receivingLine = receiving.map(p => p.name).join(', ')
    const text = [
      `JAI trade verdict: ${meta.label} (${acceptanceScore}%)`,
      `Give: ${givingLine}`,
      `Get: ${receivingLine}`,
      'via JAI',
    ].join('\n')
    shareText(text)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <V2Card pad="24px">
        <MascotVerdictHeader verdict={verdict} acceptanceScore={acceptanceScore} />
        <TradePlayersStrip giving={giving} receiving={receiving} />

        {verdict === 'DONT_PROPOSE' && unfairTo && (
          <Notice tone="error" style={{ marginTop: 0 }}>This trade is unfair to {unfairTo === 'me' ? 'you' : targetManagerName || 'them'}.</Notice>
        )}

        <ProsConsGrid pros={pros} cons={cons} />

        {impacts?.length > 0 && (
          <div style={{ marginTop: 16 }}>
            {impacts.map(row => <ImpactRow key={row.key} {...row} />)}
          </div>
        )}

        {summary && <p style={{ fontSize: 13, color: V2.mute, lineHeight: 1.6, margin: '16px 0 0' }}>{summary}</p>}

        {/* ── Footer, per verdict ── */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 18 }}>
          {verdict === 'PROPOSE' && (
            <>
              <SecondaryBtn onClick={onGenerateAnother} style={{ fontSize: 12 }}>{V2Icons.plus} Generate New Trade</SecondaryBtn>
              <PrimaryBtn onClick={() => onSave(result)} disabled={savedNote || savingNote} style={{ fontSize: 12 }}>
                {savedNote ? 'Saved to My Notes' : savingNote ? 'Saving…' : 'Save this Trade Proposal to My Notes'}
              </PrimaryBtn>
              {saveNoteError && <span style={{ fontSize: 12, color: V2.error, alignSelf: 'center' }}>{saveNoteError}</span>}
            </>
          )}
          {verdict === 'ALTER' && (
            <>
              <SecondaryBtn onClick={onAlterManually} style={{ fontSize: 12 }}>Alter Trade Manually</SecondaryBtn>
              <PrimaryBtn onClick={onLetJaiAlter} style={{ fontSize: 12 }}>Let JAI Alter the Trade</PrimaryBtn>
            </>
          )}
          {verdict === 'DONT_PROPOSE' && (
            <PrimaryBtn onClick={onLetJaiAlter} style={{ fontSize: 12 }}>Allow JAI to Generate a Trade</PrimaryBtn>
          )}
          {IS_NATIVE_APP && (
            <SecondaryBtn onClick={handleShare} style={{ fontSize: 12 }}>Share</SecondaryBtn>
          )}
          <SecondaryBtn onClick={onStartOver} style={{ fontSize: 12, marginLeft: 'auto' }}>Start over</SecondaryBtn>
        </div>
      </V2Card>

      {(verdict === 'ALTER' || verdict === 'DONT_PROPOSE') && (generated || generating || genError) && (
        <JaiGeneratedTrade
          generated={generated} generating={generating} genError={genError}
          onRequestNew={onRequestNew} onSave={onSave} saved={savedNote} saving={savingNote} saveError={saveNoteError}
        />
      )}
    </div>
  )
}
